import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact, {
  type BugsnagErrorBoundary,
  type BugsnagPluginReactResult,
} from "@bugsnag/plugin-react";
import BugsnagPerformance from "@bugsnag/browser-performance";
import React from "react";
import {
  BUGSNAG_API_KEY,
  releaseStage,
  appVersion,
  enabledReleaseStages,
  toError,
} from "./shared";

if (!Bugsnag.isStarted() && BUGSNAG_API_KEY) {
  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    plugins: [new BugsnagPluginReact()],
    releaseStage,
    appVersion,
    enabledReleaseStages,
    onError: (event) => {
      // Discards `removeChild`/`insertBefore` NotFoundError caused by browser
      // extensions (Google Translate is the canonical offender) mutating DOM
      // nodes that React owns. Only filter when the stack contains zero app
      // frames — real app errors with that name still pass through.
      const err = event.errors?.[0];
      if (!err) return;
      const msg = err.errorMessage ?? "";
      const isReactDomMutation =
        err.errorClass === "NotFoundError" &&
        /can not be found here|removeChild|insertBefore/.test(msg) &&
        Array.isArray(err.stacktrace) &&
        err.stacktrace.length > 0 &&
        err.stacktrace.every((f) => {
          const file = f.file ?? "";
          return (
            file === "" ||
            file === "[native code]" ||
            /react-dom/.test(file)
          );
        });
      if (isReactDomMutation) return false;
    },
  });
  BugsnagPerformance.start({
    apiKey: BUGSNAG_API_KEY,
    releaseStage,
    appVersion,
  });
}

const reactPlugin = BUGSNAG_API_KEY
  ? (Bugsnag.getPlugin("react") as BugsnagPluginReactResult | undefined)
  : undefined;

const PassthroughBoundary: BugsnagErrorBoundary = ({ children }) =>
  React.createElement(React.Fragment, null, children);

export const ErrorBoundary: BugsnagErrorBoundary =
  reactPlugin?.createErrorBoundary(React) ?? PassthroughBoundary;

export function setBugsnagUser(id: string, email?: string | null): void {
  Bugsnag.setUser(id, email ?? undefined, undefined);
}

export function clearBugsnagUser(): void {
  Bugsnag.setUser(undefined, undefined, undefined);
}

export function notifyClient(error: unknown, metadata?: Record<string, unknown>): void {
  if (!Bugsnag.isStarted()) return;
  Bugsnag.notify(toError(error), (event) => {
    if (metadata) event.addMetadata("context", metadata);
  });
}
